import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Avatar,
  Chip,
  Stack,
  Divider,
  Grid,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  IconButton,
  InputAdornment,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Lock as PasswordIcon,
  Email as EmailIcon,
  Person as PersonIcon,
  Security as SecurityIcon,
  CheckCircle as VerifiedIcon,
  Schedule as PendingIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import { useDataProvider, useNotify, RecordContextProvider } from 'react-admin';
import { PageTitle } from '../components/PageTitle';
import { UserTimeTrackingList } from '../resources/users/UserTimeTrackingList';
import { TenantUser } from '../types/tenant';

const getRoleColor = (role: string) => {
  switch (role) {
    case 'admin':
      return 'error' as const;
    case 'operator':
      return 'warning' as const;
    case 'user':
      return 'primary' as const;
    default:
      return 'default' as const;
  }
};

export const UserShowPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dataProvider = useDataProvider();
  const notify = useNotify();

  const [tenantUser, setTenantUser] = useState<TenantUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [passwordDialog, setPasswordDialog] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(true); // Default to showing password for admin convenience

  useEffect(() => {
    loadUser();
  }, [id]);

  const loadUser = async () => {
    try {
      // Get all tenant users and find the one we're viewing
      const response = await dataProvider.getList('tenant-users', {
        pagination: { page: 1, perPage: 100 },
        sort: { field: 'user.name', order: 'ASC' },
        filter: {},
      });

      const user = response.data.find((u: TenantUser) => u.user.id === parseInt(id || '0'));
      if (user) {
        setTenantUser(user);
      } else {
        notify('User not found', { type: 'error' });
        navigate('/users');
      }
    } catch (error) {
      notify('Error loading user', { type: 'error' });
      navigate('/users');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = () => {
    setDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    if (!tenantUser) return;

    try {
      await dataProvider.delete('tenant-users', {
        id: tenantUser.user.id,
        previousData: tenantUser,
      });

      notify(`${tenantUser.user.name || tenantUser.user.email} removed from tenant`, { type: 'success' });
      navigate('/users');
    } catch (error: any) {
      const message = error?.body?.message || 'Error removing user from tenant';
      notify(message, { type: 'error' });
    } finally {
      setDeleteDialog(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialog(false);
  };

  const handlePasswordClick = () => {
    setPasswordDialog(true);
    setNewPassword('');
    setShowPassword(true); // Reset to showing password by default
  };

  const handlePasswordConfirm = async () => {
    if (!tenantUser || !newPassword.trim()) return;

    try {
      await dataProvider.create(`tenant-users/${tenantUser.user.id}/change-password`, {
        data: { newPassword },
      });

      notify('Password changed successfully', { type: 'success' });
      setPasswordDialog(false);
      setNewPassword('');
    } catch (error: any) {
      const message = error?.body?.message || 'Error changing password';
      notify(message, { type: 'error' });
    }
  };

  const handlePasswordCancel = () => {
    setPasswordDialog(false);
    setNewPassword('');
    setShowPassword(true);
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  if (loading) {
    return (
      <Box>
        <PageTitle
          title="Loading..."
          subtitle="Loading user details"
          icon={<PersonIcon />}
        />
        <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
          <Typography>Loading user details...</Typography>
        </Container>
      </Box>
    );
  }

  if (!tenantUser) {
    return (
      <Box>
        <PageTitle
          title="User Not Found"
          subtitle="The requested user could not be found"
          icon={<PersonIcon />}
        />
      </Box>
    );
  }

  return (
    <Box>
      <PageTitle
        title="User Details"
        subtitle="View user information and tenant roles"
        icon={<PersonIcon />}
      />
      <Container maxWidth={false} sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Button
            startIcon={<BackIcon />}
            onClick={() => navigate('/users')}
            sx={{ mr: 2 }}
          >
            Back to Users
          </Button>
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={() => navigate(`/users/${id}/edit`)}
            sx={{ backgroundColor: '#1C5195', mr: 2 }}
          >
            Edit Roles
          </Button>
          <Button
            variant="outlined"
            startIcon={<PasswordIcon />}
            onClick={handlePasswordClick}
            sx={{ mr: 2 }}
          >
            Change Password
          </Button>
          <Button
            variant="outlined"
            startIcon={<DeleteIcon />}
            onClick={handleDeleteClick}
            color="error"
          >
            Remove from Tenant
          </Button>
        </Box>

        <Grid container spacing={3}>
          {/* User Profile Card */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <Avatar
                    src={tenantUser.user.avatarUrl}
                    sx={{ width: 64, height: 64, mr: 2 }}
                  >
                    {tenantUser.user.name?.charAt(0) || tenantUser.user.email.charAt(0)}
                  </Avatar>
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      {tenantUser.user.name || 'Unnamed User'}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <EmailIcon fontSize="small" color="action" />
                      <Typography color="text.secondary">
                        {tenantUser.user.email}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 120 }}>
                      Email Status:
                    </Typography>
                    <Chip
                      icon={tenantUser.user.emailVerified ? <VerifiedIcon /> : <PendingIcon />}
                      label={tenantUser.user.emailVerified ? 'Verified' : 'Pending Verification'}
                      color={tenantUser.user.emailVerified ? 'success' : 'warning'}
                      size="small"
                    />
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 120 }}>
                      User ID:
                    </Typography>
                    <Typography variant="body2">
                      {tenantUser.user.id}
                    </Typography>
                  </Box>

                  {tenantUser.user.createdAt && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 120 }}>
                        Created:
                      </Typography>
                      <Typography variant="body2">
                        {new Date(tenantUser.user.createdAt).toLocaleDateString()}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Tenant Roles Card */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <SecurityIcon sx={{ mr: 1 }} />
                  <Typography variant="h6">
                    Tenant Roles
                  </Typography>
                </Box>

                <Stack spacing={2}>
                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Current Roles:
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                      {tenantUser.roles.map((role) => (
                        <Chip
                          key={role}
                          label={role.charAt(0).toUpperCase() + role.slice(1)}
                          color={getRoleColor(role)}
                          size="medium"
                        />
                      ))}
                    </Stack>
                  </Box>

                  <Divider />

                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Role Descriptions:
                    </Typography>
                    <Box sx={{ pl: 1 }}>
                      {tenantUser.roles.includes('admin') && (
                        <Typography variant="caption" display="block" color="error.main">
                          • Admin: Full access to tenant management and settings
                        </Typography>
                      )}
                      {tenantUser.roles.includes('operator') && (
                        <Typography variant="caption" display="block" color="warning.main">
                          • Operator: Can manage operations and workflows
                        </Typography>
                      )}
                      {tenantUser.roles.includes('user') && (
                        <Typography variant="caption" display="block" color="primary.main">
                          • User: Standard access to tenant features
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6">
                  Time Tracking
                </Typography>
                <RecordContextProvider value={tenantUser.user}>
                  <UserTimeTrackingList />
                </RecordContextProvider>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialog}
          onClose={handleDeleteCancel}
          aria-labelledby="delete-dialog-title"
          aria-describedby="delete-dialog-description"
        >
          <DialogTitle id="delete-dialog-title">
            Remove User from Tenant
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="delete-dialog-description">
              Are you sure you want to remove{' '}
              <strong>{tenantUser?.user.name || tenantUser?.user.email}</strong>{' '}
              from this tenant? This action cannot be undone.
              <br /><br />
              <em>Note: This will only remove the user from this tenant. Their user account will remain active and they can still access other tenants they belong to.</em>
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDeleteCancel} color="primary">
              Cancel
            </Button>
            <Button onClick={handleDeleteConfirm} color="error" variant="contained">
              Remove User
            </Button>
          </DialogActions>
        </Dialog>

        {/* Change Password Dialog */}
        <Dialog
          open={passwordDialog}
          onClose={handlePasswordCancel}
          aria-labelledby="password-dialog-title"
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle id="password-dialog-title">
            Change Password for {tenantUser?.user.name || tenantUser?.user.email}
          </DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>
              Enter a new password for this user. The user will need to use this password to log in.
            </DialogContentText>
            <TextField
              autoFocus
              margin="dense"
              label="New Password"
              type={showPassword ? 'text' : 'password'}
              fullWidth
              variant="outlined"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              inputProps={{ minLength: 6 }}
              helperText="Password must be at least 6 characters long"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={handleTogglePasswordVisibility}
                      edge="end"
                      aria-label="toggle password visibility"
                    >
                      {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handlePasswordCancel} color="primary">
              Cancel
            </Button>
            <Button
              onClick={handlePasswordConfirm}
              color="primary"
              variant="contained"
              disabled={!newPassword.trim() || newPassword.length < 6}
            >
              Change Password
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};